// src/SallerAdmin/pages/Profile/UserProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext'; // Ensure path is correct
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, User as UserIcon, Lock, Edit, Save, Trash, Plus, X, Upload } from 'lucide-react'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Optional if mixing icons
import { faCog } from '@fortawesome/free-solid-svg-icons'; // For loading spinner

// --- Helper Components ---
const DetailCard = ({ title, children, action }: any) => (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
            {action}
        </div>
        <div className="p-5 space-y-4 flex-grow relative">
            {children}
        </div>
    </div>
);

const DetailRow = ({ icon, label, value, isPrimary=false }: any) => (
    <div className={`flex items-start space-x-3 p-2 rounded-md transition ${isPrimary ? 'bg-blue-50 border border-blue-100' : ''}`}>
        <div className="text-red-600 flex-shrink-0 mt-1">{icon}</div> 
        <div className="flex-1">
            <p className="text-xs font-bold text-gray-500 uppercase flex items-center">
                {label} {isPrimary && <span className="ml-2 text-[10px] bg-blue-600 text-white px-2 rounded-full">PRIMARY</span>}
            </p>
            <p className="text-sm font-semibold text-gray-900 break-all">{value || 'Not Added'}</p>
        </div>
    </div>
);

const UserProfilePage: React.FC = () => {
    const { user, updateUser } = useUser(); 
    
    // States
    const [loading, setLoading] = useState(false);
    const [addresses, setAddresses] = useState<any[]>([]);
    
    // Modals
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPassModal, setShowPassModal] = useState(false);
    const [showAddrModal, setShowAddrModal] = useState(false);

    // Form Data
    const [editData, setEditData] = useState<any>({});
    const [passData, setPassData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [newAddress, setNewAddress] = useState({ addressLine1: '', city: '', state: '', postalCode: '' });
    const [locLoading, setLocLoading] = useState(false);

    // --- INIT ---
    useEffect(() => {
        if(user) {
            setEditData({
                username: user.username,
                mobile: user.mobile,
                alternateMobile: user.alternateMobile || '',
                email: user.email,
                alternateEmail: user.alternateEmail || '',
                companyName: user.companyName
            });
            fetchAddresses();
        }
    }, [user]);

    const getToken = () => localStorage.getItem("token");

    // --- API CALLS ---
    const fetchAddresses = async () => {
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/addresses`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if(res.ok) setAddresses(await res.json());
        } catch(e) { console.error(e); }
    };

    // --- HANDLERS: PROFILE EDIT ---
    const handleEditChange = (e: any) => {
        setEditData({...editData, [e.target.name]: e.target.value});
    };

    const handleSwapMobile = () => {
        setEditData({
            ...editData,
            mobile: editData.alternateMobile,
            alternateMobile: editData.mobile
        });
    };

    const handleSwapEmail = () => {
        setEditData({
            ...editData,
            email: editData.alternateEmail,
            alternateEmail: editData.email
        });
    };

    const saveProfile = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify(editData)
            });
            if(res.ok) {
                const updated = await res.json();
                updateUser(updated); // Update Context
                setShowEditModal(false);
                alert("Profile Updated Successfully!");
            }
        } catch(e) { alert("Failed to update"); }
        finally { setLoading(false); }
    };

    // --- HANDLERS: PASSWORD ---
    const changePassword = async () => {
        if(passData.newPassword !== passData.confirmPassword) return alert("New passwords do not match");
        setLoading(true);
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify(passData)
            });
            if(res.ok) {
                alert("Password changed! Email notification sent.");
                setShowPassModal(false);
                setPassData({ oldPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                alert(await res.text());
            }
        } catch(e) { alert("Error changing password"); }
        finally { setLoading(false); }
    };

    // --- HANDLERS: ADDRESS ---
    const handlePincodeChange = async (e: any) => {
        const val = e.target.value.replace(/\D/g, '');
        setNewAddress({...newAddress, postalCode: val});
        if(val.length === 6) {
            setLocLoading(true);
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
                const data = await res.json();
                if(data[0].Status === "Success") {
                    setNewAddress(prev => ({...prev, postalCode: val, city: data[0].PostOffice[0].District, state: data[0].PostOffice[0].State}));
                }
            } finally { setLocLoading(false); }
        }
    };

    const saveAddress = async () => {
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/addresses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify(newAddress)
            });
            if(res.ok) {
                fetchAddresses();
                setShowAddrModal(false);
                setNewAddress({ addressLine1: '', city: '', state: '', postalCode: '' });
            }
        } catch(e) { console.error(e); }
    };

    const deleteAddress = async (id: number) => {
        if(!window.confirm("Delete this address?")) return;
        await fetch(`/springboot/api/seller/profile/addresses/${id}`, { method: 'DELETE', headers: {'Authorization': `Bearer ${getToken()}`} });
        fetchAddresses();
    };

    if(!user) return <div>Loading...</div>;

    const userInitial = user.username ? user.username.charAt(0).toUpperCase() : 'S';

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-3">User Profile (Personal Details)</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* --- 1. Account Holder --- */}
                <DetailCard 
                    title="Account Holder Details"
                    action={<button onClick={()=>setShowEditModal(true)} className="text-blue-600 text-sm font-semibold hover:underline flex items-center"><Edit size={14} className="mr-1"/> Edit</button>}
                >
                    <div className="flex items-center space-x-4 pb-4 border-b border-gray-100 mb-2">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold text-2xl shadow-lg border-2 border-white">
                            {userInitial}
                        </div>
                        <div>
                            <p className="text-xl font-bold text-gray-900">{user.username}</p>
                            <p className="text-sm text-gray-500">{user.companyName || 'Seller Account'}</p>
                        </div>
                    </div>
                    
                    <DetailRow icon={<Mail size={16} />} label="Primary Email" value={user.email} isPrimary />
                    <DetailRow icon={<Mail size={16} />} label="Alternate Email" value={user.alternateEmail} />
                    <DetailRow icon={<Phone size={16} />} label="Contact Number" value={user.mobile} isPrimary />
                    <DetailRow icon={<Phone size={16} />} label="Alternate Contact" value={user.alternateMobile} />
                </DetailCard>

                {/* --- 2. Security --- */}
                <DetailCard title="Account Security">
                    {/* Hiding User ID as requested */}
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center mb-2 text-yellow-800 font-bold">
                            <Lock size={16} className="mr-2"/> Password Status
                        </div>
                        <p className="text-xs text-yellow-700 mb-3">
                            Regular password updates protect your business account.
                        </p>
                        <button onClick={()=>setShowPassModal(true)} className="w-full bg-white border border-yellow-600 text-yellow-700 py-2 rounded text-sm font-bold hover:bg-yellow-100 transition">
                            Change Password
                        </button>
                    </div>
                </DetailCard>

                {/* --- 3. Addresses --- */}
                <DetailCard 
                    title="Saved Addresses"
                    action={<button onClick={()=>setShowAddrModal(true)} className="text-green-600 text-sm font-semibold hover:underline flex items-center"><Plus size={14} className="mr-1"/> Add New</button>}
                >
                    {addresses.length === 0 ? (
                        <div className="text-center text-gray-400 py-6">No addresses saved.</div>
                    ) : (
                        <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                            {addresses.map((addr, idx) => (
                                <div key={idx} className="p-3 border rounded bg-gray-50 relative group">
                                    <p className="font-bold text-sm text-gray-800">{addr.city}, {addr.postalCode}</p>
                                    <p className="text-xs text-gray-600 truncate">{addr.addressLine1}</p>
                                    <button onClick={()=>deleteAddress(addr.id)} className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition">
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </DetailCard>
            </div>

            {/* --- MODAL: EDIT PROFILE --- */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6">
                        <div className="flex justify-between items-center mb-4 border-b pb-2">
                            <h2 className="text-xl font-bold text-gray-800">Edit Personal Details</h2>
                            <button onClick={()=>setShowEditModal(false)}><X className="text-gray-500 hover:text-red-500" /></button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Mobile Section */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <label className="block text-xs font-bold text-blue-800 mb-1">PRIMARY MOBILE</label>
                                <input disabled value={editData.mobile} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-600 mb-3" />
                                
                                <label className="block text-xs font-bold text-gray-600 mb-1">ALTERNATE MOBILE</label>
                                <input name="alternateMobile" value={editData.alternateMobile} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 mb-2" />
                                
                                {editData.alternateMobile && (
                                    <button onClick={handleSwapMobile} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 w-full">Make Alternate Primary</button>
                                )}
                            </div>

                            {/* Email Section */}
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <label className="block text-xs font-bold text-purple-800 mb-1">PRIMARY EMAIL</label>
                                <input disabled value={editData.email} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-600 mb-3" />
                                
                                <label className="block text-xs font-bold text-gray-600 mb-1">ALTERNATE EMAIL</label>
                                <input name="alternateEmail" value={editData.alternateEmail} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2 mb-2" />
                                
                                {editData.alternateEmail && (
                                    <button onClick={handleSwapEmail} className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700 w-full">Make Alternate Primary</button>
                                )}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Full Name / Username</label>
                                <input name="username" value={editData.username} onChange={handleEditChange} className="w-full border border-gray-300 rounded p-2" />
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button onClick={saveProfile} disabled={loading} className="bg-red-600 text-white px-6 py-2 rounded font-bold hover:bg-red-700">
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL: CHANGE PASSWORD --- */}
            {showPassModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Change Password</h2>
                        <div className="space-y-4">
                            <input type="password" placeholder="Old Password" value={passData.oldPassword} onChange={(e)=>setPassData({...passData, oldPassword: e.target.value})} className="w-full border p-2 rounded" />
                            <input type="password" placeholder="New Password" value={passData.newPassword} onChange={(e)=>setPassData({...passData, newPassword: e.target.value})} className="w-full border p-2 rounded" />
                            <input type="password" placeholder="Confirm New Password" value={passData.confirmPassword} onChange={(e)=>setPassData({...passData, confirmPassword: e.target.value})} className="w-full border p-2 rounded" />
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button onClick={()=>setShowPassModal(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                            <button onClick={changePassword} disabled={loading} className="bg-yellow-600 text-white px-4 py-2 rounded font-bold">Update Password</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL: ADD ADDRESS --- */}
            {showAddrModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
                         <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Add New Address</h2>
                            <button onClick={()=>setShowAddrModal(false)}><X /></button>
                        </div>
                        <div className="space-y-4">
                            <div className="relative">
                                <input placeholder="Pincode (6 Digits)" value={newAddress.postalCode} onChange={handlePincodeChange} maxLength={6} className="w-full border p-2 rounded pr-8" />
                                {locLoading && <FontAwesomeIcon icon={faCog} spin className="absolute right-3 top-3 text-gray-500" />}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <input placeholder="City" value={newAddress.city} readOnly className="border p-2 rounded bg-gray-50" />
                                <input placeholder="State" value={newAddress.state} readOnly className="border p-2 rounded bg-gray-50" />
                            </div>
                            <textarea placeholder="Address Line 1 (Street, Building)" value={newAddress.addressLine1} onChange={(e)=>setNewAddress({...newAddress, addressLine1: e.target.value})} className="w-full border p-2 rounded" rows={3} />
                        </div>
                        <button onClick={saveAddress} className="w-full mt-4 bg-green-600 text-white py-2 rounded font-bold">Save Address</button>
                    </div>
                </div>
            )}
            
            {/* Footer Navigation */}
            <div className="mt-8 pt-6 border-t border-gray-300 flex justify-start space-x-6">
                 <Link to="/seller/profile/business" className="text-base font-semibold text-blue-600 hover:text-red-700">Go to Business Profile &rarr;</Link>
            </div>
        </div>
    );
};

export default UserProfilePage;